type Page = { slug?: string; sections?: { type?: string; specifics?: any }[] };

export type SearchSite = {
  pages?: Page[];
  defaultSearchPage?: string | null;
};

/** A list that shows what the page was searched for: the classic CaseList, or a Sagsliste whose »Vis
 *  søgeresultater« is on. */
function searchesHere(page: Page | undefined): boolean {
  return !!page?.sections?.some(
    (section) =>
      section?.type === "CaseList" ||
      (section?.type === "PropertyListSection" &&
        section.specifics?.useSearch !== false),
  );
}
function activeSearchList(page: Page): boolean {
  return !!page.sections?.some(
    (section) =>
      section?.type === "PropertyListSection" &&
      section.specifics?.useSearch !== false &&
      (section.specifics?.propertyStatus ?? "active") === "active",
  );
}
/** The first page with a Sagsliste for sale that searches; the front page only when no other page has one. */
function firstSearchListPage(pages: Page[]): string | undefined {
  const candidates = pages.filter(
    (page) => typeof page.slug === "string" && activeSearchList(page),
  );
  return (candidates.find((page) => page.slug !== "/") ?? candidates[0])?.slug;
}

/** Where a search box sends its word: the current page when it has a list that searches (never the front page), else
 *  the website's search page, else the first page with a Sagsliste for sale, else /koeb. A site without a Sagsliste
 *  goes exactly where it went before. */
export function searchTarget(
  cmsData: SearchSite | undefined,
  currentRoute: string,
): string {
  const pages = cmsData?.pages ?? [];
  const current = pages.find((page) => page.slug === currentRoute);
  let target =
    searchesHere(current) && currentRoute !== "/"
      ? currentRoute
      : (cmsData?.defaultSearchPage ?? firstSearchListPage(pages) ?? "/koeb");
  if (!target.startsWith("/")) target = `/${target}`;
  return target;
}

/** Only the pages' lists, so a search box on the page does not ship the whole website to the browser. A search box's
 *  own result page takes the place of the website's search page. */
export function searchSite(
  cmsData: SearchSite | undefined,
  targetPage?: string,
): SearchSite {
  return {
    defaultSearchPage:
      typeof targetPage === "string" && targetPage
        ? targetPage
        : cmsData?.defaultSearchPage,
    pages: (cmsData?.pages ?? []).map((page) => ({
      slug: page?.slug,
      sections: (page?.sections ?? [])
        .filter(
          (section) =>
            section?.type === "CaseList" ||
            section?.type === "PropertyListSection",
        )
        .map((section) => ({
          type: section.type,
          specifics: {
            useSearch: section.specifics?.useSearch,
            propertyStatus: section.specifics?.propertyStatus,
          },
        })),
    })),
  };
}
